import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

const adaptUser = (user) => {
  if (!user) return null;
  const names = user.name.trim().split(/\s+/);
  return {
    ...user,
    firstName: names[0] || '',
    lastName: names.slice(1).join(' '),
    primaryEmailAddress: { emailAddress: user.email },
    getToken: async () => null,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.me();
      const authenticatedUser = adaptUser(response.data.data);
      setUser(authenticatedUser);
      return authenticatedUser;
    } catch (error) {
      if (error.response?.status !== 401) throw error;
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setIsLoaded(true));
  }, [refreshUser]);

  const login = useCallback(async (credentials) => {
    await authAPI.csrf();
    const response = await authAPI.login(credentials);
    const authenticatedUser = adaptUser(response.data.data);
    setUser(authenticatedUser);
    return authenticatedUser;
  }, []);

  const register = useCallback(async (registration) => {
    await authAPI.csrf();
    const response = await authAPI.register(registration);
    const authenticatedUser = adaptUser(response.data.data);
    setUser(authenticatedUser);
    return authenticatedUser;
  }, []);

  const logout = useCallback(async () => {
    await authAPI.logout();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    isLoaded,
    isSignedIn: Boolean(user),
    login,
    register,
    logout,
    refreshUser,
    getToken: async () => null,
  }), [isLoaded, login, logout, refreshUser, register, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuthentication = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('Authentication hooks must be used inside AuthProvider.');
  return context;
};

export const useUser = useAuthentication;
export const useAuth = useAuthentication;
export const SignedIn = ({ children }) => useAuthentication().isSignedIn ? children : null;
export const SignedOut = ({ children }) => useAuthentication().isSignedIn ? null : children;

export const UserButton = () => {
  const { logout, user } = useAuthentication();
  return (
    <button type="button" onClick={logout} className="rounded-full bg-yellow-400 px-3 py-2 text-sm font-bold text-black" aria-label="Sign out">
      {user?.firstName?.charAt(0).toUpperCase() || 'U'}
    </button>
  );
};
